/**
 * Key features: reveal on scroll, videos play only while visible, and rail
 * navigation (arrows, counter, progress) when the section uses the rail layout.
 */
class SolsticeKeyFeatures extends HTMLElement {
  #observers = [];
  #track = null;
  #frame = 0;
  /** @type {number | null} Arrow target while a smooth scroll is running. */
  #target = null;

  connectedCallback() {
    this.#track = this.querySelector('.solstice__key-features-items');
    const items = [...this.querySelectorAll('.solstice__key-features-item')];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!reduceMotion && 'IntersectionObserver' in window) {
      this.classList.add('is-animated');
      // Grid items reveal individually; rail items reveal together when the
      // rail enters the viewport, so horizontal scrolling never waits on them.
      const isRail = this.dataset.layout === 'rail';
      const reveal = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const targets = isRail ? items : [entry.target];
            targets.forEach((item) => item.classList.add('is-visible'));
            reveal.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -10% 0px' }
      );
      if (isRail && this.#track) {
        reveal.observe(this.#track);
      } else {
        items.forEach((item) => reveal.observe(item));
      }
      this.#observers.push(reveal);
    }

    const videos = this.querySelectorAll('video');
    if (videos.length && 'IntersectionObserver' in window) {
      const playback = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const video = /** @type {HTMLVideoElement} */ (entry.target);
            if (entry.isIntersecting && !reduceMotion) {
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          }
        },
        { threshold: 0.35 }
      );
      videos.forEach((video) => playback.observe(video));
      this.#observers.push(playback);
    }

    if (this.dataset.layout === 'rail' && this.#track) {
      this.addEventListener('click', this.#onClick);
      this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#track.addEventListener('scrollend', this.#onScrollEnd);
      this.#track.addEventListener('pointerdown', this.#onScrollEnd);
      window.addEventListener('resize', this.#onScroll, { passive: true });
      this.#update();
    }
  }

  disconnectedCallback() {
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
    this.removeEventListener('click', this.#onClick);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#track?.removeEventListener('scrollend', this.#onScrollEnd);
    this.#track?.removeEventListener('pointerdown', this.#onScrollEnd);
    window.removeEventListener('resize', this.#onScroll);
    cancelAnimationFrame(this.#frame);
  }

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-direction]') : null;
    if (!button || !this.#track) return;
    const { step, total, max } = this.#metrics();
    // Step from the target position, not the mid-animation scroll position, so
    // rapid clicks each advance exactly one step.
    const base = this.#target ?? Math.round(this.#track.scrollLeft / step);
    const target = Math.max(0, Math.min(total - 1, base + Number(button.dataset.direction)));
    this.#target = target;
    this.#render(target + 1, total);
    this.#track.scrollTo({ left: Math.min(max, target * step), behavior: 'smooth' });
  };

  #metrics() {
    const track = /** @type {HTMLElement} */ (this.#track);
    const max = track.scrollWidth - track.clientWidth;
    const item = track.querySelector('.solstice__key-features-item');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = (item?.getBoundingClientRect().width || track.clientWidth) + gap;
    // Positions an arrow click can reach: the start plus one per step.
    const total = max > 1 ? Math.ceil((max - 1) / step) + 1 : 1;
    return { step, total, max };
  }

  #render(current, total) {
    const pad = (value) => String(value).padStart(2, '0');
    const currentLabel = this.querySelector('[data-current]');
    const totalLabel = this.querySelector('[data-total]');
    if (currentLabel) currentLabel.textContent = pad(current);
    if (totalLabel) totalLabel.textContent = pad(total);
    this.style.setProperty('--key-features-progress', String(current / total));
    const [prev, next] = this.querySelectorAll('[data-direction]');
    if (prev) prev.toggleAttribute('disabled', current <= 1);
    if (next) next.toggleAttribute('disabled', current >= total);
  }

  /** A finished scroll or a manual drag ends arrow tracking. */
  #onScrollEnd = () => {
    this.#target = null;
    this.#onScroll();
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const track = this.#track;
    if (!track) return;
    const { step, total, max } = this.#metrics();
    const atEnd = track.scrollLeft >= max - 1;
    const position = atEnd ? total : Math.min(total, Math.round(track.scrollLeft / step) + 1);
    if (this.#target !== null && position - 1 !== this.#target && !atEnd) return;
    this.#target = null;
    this.#render(position, total);
  }
}

if (!customElements.get('solstice-key-features')) {
  customElements.define('solstice-key-features', SolsticeKeyFeatures);
}
